import { FileOpener } from '@awesome-cordova-plugins/file-opener'
import { Capacitor } from '@capacitor/core'
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem'
import { SynerallInvoice } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import dayjs, { Dayjs } from 'dayjs'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import axiosInstance from '../../../authentication/axiosInstance'

const getRowValues = (
	csv: string,
	start: Dayjs,
	end: Dayjs,
	invoiceData: string | null | undefined
) => {
	if (invoiceData) {
		const lines = csv.split('\n')

		const timestampRowRegex = /^\d{2}\.\d{2}\.\d{4} \d{1,2}\.\d{2}/
		const rowData: { date: string; kwh: number; vatTotal: number; noVatTotal: number }[] = []

		for (const line of lines) {
			const isTimestampRow = timestampRowRegex.test(line)

			if (!isTimestampRow) continue
			const cells = line.split(';')
			const dateCell = dayjs(cells[0], 'DD.MM.YYYY H.mm')

			if (!dateCell.isBetween(start, end, null, '[]')) continue
			const kwhCell = Number(cells[1].replace(',', '.'))
			const noVatTotalCell = Number(cells[3].replace(',', '.'))
			const vatPercent = dateCell.isBefore('2024-09-01') ? 1.24 : 1.255

			rowData.push({
				kwh: kwhCell,
				vatTotal: noVatTotalCell * vatPercent,
				noVatTotal: noVatTotalCell,
				date: dateCell.toISOString(),
			})
		}

		return rowData
	}

	return []
}
export const downloadPDF = async (invoice: SynerallInvoice) => {
	logEvent('click-invoice-download')
	const blobToBase64 = (blob: any) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onloadend = () => {
				const base64String = reader.result
				resolve(base64String)
			}
			reader.onerror = reject
			reader.readAsDataURL(blob)
		})
	}

	axiosInstance
		.get(`invoices/${invoice.Number}/pdf`, {
			responseType: 'blob',
		})
		.then(async response => {
			const blob = new Blob([response.data])
			if (Capacitor.isNativePlatform()) {
				const base64 = await blobToBase64(blob)

				const { uri } = await Filesystem.writeFile({
					path: `invoice-${invoice.Number}-${dayjs().unix()}.pdf`,
					data: base64 as string,
					directory: Directory.Documents,
				})

				FileOpener.open(uri, 'application/pdf')
					.then(() => console.log('File is opened'))
					.catch(async (e: any) => {
						console.log('Error opening file with default app trying with dialog...', e)
						FileOpener.showOpenWithDialog(uri, 'application/pdf')
							.then(() => 'File opened with dialog')
							.catch(() => console.log('Error opening file with dialog'))
					})
			} else {
				const blobUrl = URL.createObjectURL(blob)
				// Create a temporary <a> element to trigger download
				const link = document.createElement('a')
				link.href = blobUrl
				link.download = `invoice-${invoice.Number}-${dayjs().unix()}.pdf`

				// Append to body, trigger click, and clean up
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(blobUrl)
			}
		})
		.catch(error => {
			console.error('Error downloading PDF:', error)
		})
}

export const downloadBreakdown = async (
	invoiceData: string | null | undefined,
	invoice: SynerallInvoice,
	spotMarginal: number
) => {
	if (!invoiceData) return

	logEvent('click-invoice-downloadBreakdown')

	const lines = invoiceData.split('\n')

	const timestampRowRegex = /^\d{2}\.\d{2}\.\d{4} \d{1,2}\.\d{2}/

	const sanitizeLine = (line: string) =>
		line
			.replace(/[\n\r]/g, '') // Remove newlines
			.replace(/(\d),(?=\d)/g, '$1.') // Replace decimal commas with dots
			// eslint-disable-next-line no-irregular-whitespace
			.replace(/ /g, ' ') // Remove non-breaking spaces
			.replace('€', 'euroa')
			.replace(/;/g, ',') // Standardize delimiter
			.replaceAll('Määrä', 'Hinta')
			.replace(
				'Hinta (ALV 0%) euroa',
				'Hinta (ALV 0%) Snt.,Hinta (ALV) snt/kWh,Hinta (ALV) Snt.,Hinta (ALV + marginaali) snt/kWh,Hinta (ALV + marginaali) Snt.'
			)

	const calculateRowData = (
		data: string,
		periodStart: Dayjs,
		periodEnd: Dayjs,
		spotMarginal: number
	) => {
		const getRowData = getRowValues(data, periodStart, periodEnd, invoiceData)
		const totals = getRowData.reduce(
			(acc, row) => {
				acc.vatTotal += row.vatTotal
				acc.vatAndMarginTotal += row.vatTotal + row.kwh * ((spotMarginal ?? 0) / 100)
				acc.noVatTotal += row.noVatTotal
				acc.kwh += row.kwh
				return acc
			},
			{ vatTotal: 0, vatAndMarginTotal: 0, noVatTotal: 0, kwh: 0 }
		)

		return {
			...totals,
			weightedAverageVat: totals.kwh ? (totals.vatTotal * 100) / totals.kwh : 0,
		}
	}

	const processedLines = lines.map((line, index) => {
		let formattedRow = sanitizeLine(line)

		if (line.startsWith('Nord Pool Spot')) {
			formattedRow = formattedRow.split(',').slice(0, 3).join(',').replace('snt/kWh,', '')
		}

		if (formattedRow.startsWith(',Kulutus')) {
			formattedRow +=
				',Painotettu keskiarvo (ALV) snt/kWh,Hinta Yht. (ALV 0%) euroa,Hinta Yht. (ALV) euroa,Hinta Yht. (ALV + marginaali) euroa,\r'
		}

		if (formattedRow.startsWith('Yleis,')) {
			const dates = lines[index - 2].split(',')[2].trim().split(' - ')
			const periodStart = dayjs(dates[0].trim(), 'DD.MM.YYYY', 'fi')
			const periodEnd = dayjs(dates[1].trim(), 'DD.MM.YYYY', 'fi').endOf('day')
			const { weightedAverageVat, noVatTotal, vatTotal, vatAndMarginTotal } = calculateRowData(
				invoiceData,
				periodStart,
				periodEnd,
				spotMarginal
			)

			formattedRow += `,${weightedAverageVat.toFixed(3)},${noVatTotal},${vatTotal},${vatAndMarginTotal}\r`
		}

		const isTimestampRow = timestampRowRegex.test(line)
		if (!isTimestampRow) return formattedRow

		const cells = formattedRow.split(',')
		const dateCell = dayjs(cells[0])
		const kwh = Number(cells[1])
		const noVatPrice = Number(cells[2])
		const noVatTotal = Number(cells[3])

		// Convert Hinta (ALV 0%) from euros to cents
		cells[3] = (noVatTotal * 100).toFixed(7)
		const modifiedFormattedRow = cells.join(',')

		const vatPercent = dateCell.isBefore('2024-09-01') ? 1.24 : 1.255
		const withVatTotal = noVatTotal * vatPercent
		const withVatPrice = noVatPrice * vatPercent
		const withMarginVatPrice = withVatPrice + spotMarginal
		const withMarginTotal = withMarginVatPrice * kwh

		// Convert Hinta (ALV) from euros to cents
		return `${modifiedFormattedRow},${withVatPrice.toFixed(3)},${(withVatTotal * 100).toFixed(7)},${withMarginVatPrice.toFixed(3)},${withMarginTotal.toFixed(7)}\r`
	})

	const formatted = processedLines.join('\n')

	if (!Capacitor.isNativePlatform()) {
		const blob = new Blob([formatted], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)

		const link = document.createElement('a')
		link.href = url
		link.download = `invoice-breakdown-${invoice.Number}.csv`

		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)

		// Clean up the URL object
		setTimeout(() => URL.revokeObjectURL(url), 100)
	} else {
		// Mobile platform download
		try {
			// For mobile, use CRLF as line breaks instead of just LF
			// Many mobile CSV viewers expect Windows-style CRLF line endings
			const formattedMobile = processedLines.join('\r\n')

			// Add BOM for UTF-8 encoding
			const bom = '\uFEFF'
			const csvContent = bom + formattedMobile

			// Write directly to file system with explicit CRLF line endings
			// Avoiding the base64 encoding to preserve line breaks
			await Filesystem.writeFile({
				path: `invoice-breakdown-${invoice.Number}-${dayjs().unix()}.csv`,
				data: csvContent,
				directory: Directory.Documents,
				encoding: Encoding.UTF8,
			})
				.then(result => {
					FileOpener.open(result.uri, 'text/csv')
						.then(() => console.log('File is opened'))
						.catch(async e => {
							console.log('Error opening file with default app trying with dialog...', e)
							FileOpener.showOpenWithDialog(result.uri, 'text/csv')
								.then(() => console.log('File opened with dialog'))
								.catch(err => console.log('Error opening file with dialog', err))
						})
				})
				.catch(error => {
					console.error('Error writing file:', error)
				})
		} catch (error) {
			console.error('Error writing file:', error)
		}
	}
}
